<template>
    <div class="flex flex-col gap-4">
        <div class="flex items-center justify-between gap-2">
            <h3 class="text-muval-gray-1 text-sm font-medium">Files</h3>
            <FileViewToggle
                v-if="files.length"
                v-model="view"
            />
        </div>

        <p
            v-if="sourceDocument?.missing"
            class="text-muval-gray-2 text-sm"
        >
            The original resource document has been deleted.
        </p>
        <MuvalButton
            v-else-if="sourceDocument"
            type="button"
            quinary
            @click="openSourceDocument"
            >Open original resource document</MuvalButton
        >
        <!--
            Attachments are damage photos, compliance certificates, service
            invoices. Served through short-lived signed URLs rather than public
            object URLs: one per click, or per photo card as it comes into view.
        -->
        <FileDropZone
            v-if="canModify"
            @select="openFileDialog()"
            @drop="upload"
        />

        <MuvalLoader
            v-if="loading"
            class="mx-auto my-6"
            size="l"
        />

        <p
            v-else-if="!files.length && !pending.length"
            class="text-muval-gray-2 py-6 text-center text-sm"
            data-test="company-diary-files-empty"
        >
            No files on this entry.
        </p>

        <!-- No CSS transition in thumbnail view, so the rows leave at once rather than sliding out over the grid. -->
        <TransitionGroup
            name="list"
            tag="div"
            class="flex flex-col gap-2 empty:hidden"
            :css="view !== 'thumbnail'"
        >
            <PendingFileRow
                v-for="item in pending"
                :key="`pending-${item.id}`"
                :file="item"
                @retry="retry(item)"
                @dismiss="dismiss(item.id)"
            />

            <template v-if="view !== 'thumbnail'">
                <FileListRow
                    v-for="file in files"
                    :key="file.id"
                    :file="file"
                    :can-edit="canModify"
                    :can-delete="canModify"
                    @open="viewer?.openAt(file)"
                    @download="downloadFile(file)"
                    @edit="openEditModal(file)"
                    @delete="confirmDelete(file)"
                />
            </template>
        </TransitionGroup>

        <!-- Entry files are private and the list's `url` is unsigned, so each image card asks for its own short-lived signed link. -->
        <FileGrid
            v-if="view === 'thumbnail'"
            :files="files"
            :can-edit="() => canModify"
            :can-delete="() => canModify"
            :original-thumbnails="false"
            :resolve-thumbnail-url="resolvePreviewUrl"
            @open="(file) => viewer?.openAt(file)"
            @download="downloadFile"
            @edit="openEditModal"
            @delete="confirmDelete"
            @refresh="fetchFiles"
        />

        <FileViewerModal
            ref="viewer"
            :files="files"
            :resolve-preview-url="resolvePreviewUrl"
            @open="openFile"
            @download="downloadFile"
            @refresh="fetchFiles"
        />

        <FileDeleteModal
            v-model="isDeleteModalOpen"
            :file="fileToDelete"
            :loading="isDeleting"
            @confirm="removeFile"
        />

        <FileEditModal
            v-model="isEditModalOpen"
            :file="fileToEdit"
            :save="saveFile"
            @saved="handleEditSaved"
        />
    </div>
</template>

<script setup lang="ts">
import { useFileDialog } from '@vueuse/core';
import { toast } from 'vue-sonner';
import companyDiaryApi from '@/api/companyDiary';
import { apiErrorMessage } from '@/api/errorHandler';
import FileDeleteModal from '@components/files/FileDeleteModal.vue';
import FileDropZone from '@components/files/FileDropZone.vue';
import FileEditModal, { type FileEditValues } from '@components/files/FileEditModal.vue';
import FileGrid from '@components/files/FileGrid.vue';
import FileListRow from '@components/files/FileListRow.vue';
import FileViewerModal from '@components/files/FileViewerModal.vue';
import FileViewToggle from '@components/files/FileViewToggle.vue';
import PendingFileRow from '@components/files/PendingFileRow.vue';
import MuvalLoader from '@components/loader/MuvalLoader.vue';
import { useFileListView } from '@composables/useFileListView';
import { useVaporFileUploader } from '@composables/useVaporFileUploader';
import { getFileDisplayName, getFileExtension, type PendingFile } from '@utilities/fileDisplay';

/**
 * The entry's attachments (spec company-diary-entries, "Notes and file
 * attachments").
 *
 * Uploads go straight to S3 through a signed URL and are only then registered
 * against the entry, which is what keeps a large photo off the API's request
 * limit. Reads go the other way - a signed URL per click, never a stored public
 * one. Rows, upload target and dialogs are the shared `components/files` set, so
 * this list and the order Uploads tab cannot drift apart.
 */

const sourceDocument = ref<{ missing: boolean } | null>(null);
async function openSourceDocument() {
    try {
        const { data } = await window.tenantAxios.get(`/company-diary/entries/${props.entryId}/source-file`);
        window.open(data.download_url, '_blank', 'noopener');
    } catch {
        toast.error('You cannot open this resource document, or it is no longer available.');
    }
}

const props = defineProps<{
    /** Null while the entry is unsaved; files need something to hang off. */
    entryId: number | null;
    canModify: boolean;
}>();

const files = ref<any[]>([]);
const pending = ref<PendingFile[]>([]);
const loading = ref(false);
const isDeleteModalOpen = ref(false);
const isDeleting = ref(false);
const fileToDelete = ref<any>(null);
const isEditModalOpen = ref(false);
const fileToEdit = ref<any>(null);
const viewer = ref<InstanceType<typeof FileViewerModal> | null>(null);
const view = useFileListView();

let pendingId = 0;
let fetchSeq = 0;

/** Mirrors `StoreCompanyDiaryEntryFileRequest`, so a file the API would refuse is never sent to S3. */
const ACCEPTED_TYPES = new Set([
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/png',
    'image/heic',
    'image/webp',
]);
const MAX_SIZE_KB = 20480;

/** Why the entry won't take this file, or null when it will. */
const rejectionReason = (file: File): string | null => {
    if (!ACCEPTED_TYPES.has(file.type)) return 'Files must be a PDF, DOC, DOCX, JPG, PNG, HEIC or WEBP.';
    if (Math.ceil(file.size / 1024) > MAX_SIZE_KB) return 'Files must be 20 MB or smaller.';

    return null;
};

const fetchFiles = async () => {
    // Only the latest request's list is applied: one sent later already holds whatever an earlier one would.
    const seq = ++fetchSeq;

    if (!props.entryId) {
        files.value = [];

        return;
    }

    loading.value = true;
    try {
        const { data } = await companyDiaryApi.indexFiles(props.entryId);

        if (seq !== fetchSeq) return;

        sourceDocument.value = data.source_document || null;
        files.value = data.data ?? [];
    } catch {
        if (seq === fetchSeq) toast.error('Failed to load files.');
    } finally {
        if (seq === fetchSeq) loading.value = false;
    }
};

watch(() => props.entryId, fetchFiles, { immediate: true });

const { open: openFileDialog, onChange } = useFileDialog({ multiple: true });

onChange((selected) => {
    if (selected) upload(Array.from(selected));
});

const dismiss = (id: string | number) => {
    pending.value = pending.value.filter((item) => item.id !== id);
};

/**
 * One pending row and one upload per file, so a failure names the file that
 * failed and a retry can't re-attach the ones that made it. A file the entry
 * won't take gets a toast instead of a row. Rows are removed once the list that
 * holds their file has loaded.
 */
const upload = async (selected: File[]) => {
    const entryId = props.entryId;

    if (!entryId || !selected.length) return;

    const accepted = selected.filter((file) => {
        const reason = rejectionReason(file);

        if (reason) toast.error(`${file.name} can't be attached. ${reason}`);

        return !reason;
    });

    // Reactive, or status and progress written through `row` never reach the template.
    const rows = accepted.map((file) =>
        reactive<PendingFile>({
            id: pendingId++,
            file,
            name: file.name,
            status: 'preparing',
            loaded: 0,
            total: file.size,
            percent: 0,
        }),
    );

    pending.value = [...pending.value, ...rows];

    const attached = await Promise.all(rows.map((row) => send(row, entryId)));

    await settle(rows.filter((_, index) => attached[index]));
};

/** Re-runs one failed row on its own, without the file being re-picked. */
const retry = async (row: PendingFile) => {
    const entryId = props.entryId;

    if (!entryId) return;

    row.status = 'preparing';
    row.loaded = 0;
    row.percent = 0;

    if (await send(row, entryId)) await settle([row]);
};

/** One refetch per drop, so a large drop isn't a refetch and a toast per file. */
const settle = async (attached: PendingFile[]) => {
    if (!attached.length) return;

    await fetchFiles();
    attached.forEach((row) => dismiss(row.id));
    toast.success(attached.length > 1 ? 'Files uploaded.' : 'File uploaded.');
};

/** True once the file is attached; a failure is reported here rather than thrown. */
const send = async (row: PendingFile, entryId: number): Promise<boolean> => {
    try {
        const {
            sign,
            store,
            upload: register,
        } = useVaporFileUploader({
            signUrl: '/company-diary/entries/vapor/signed-storage-url',
            bucket: import.meta.env.VITE_BUCKET_UPLOAD,
            scope: 'tenant',
            uploadUrl: `/company-diary/entries/${entryId}/files`,
        });

        const signed = await sign([row.file]);

        row.status = 'uploading';

        const stored = await store([row.file], signed, ({ loaded, total }) => {
            row.loaded = loaded;
            row.total = total || row.total;
            row.percent = row.total ? Math.round((loaded / row.total) * 100) : 0;
        });

        await register(stored, {});

        return true;
    } catch (err: any) {
        // Refused by the API, so a retry would only be refused again. `tenantAxios` rejects with `processError()`'s shape.
        if (err?.code === 422) {
            dismiss(row.id);
            toast.error(`${row.name} can't be attached. ${apiErrorMessage(err, '')}`.trim());

            return false;
        }

        row.status = 'error';

        // An HTTP failure other than a 422 has had its toast from `processError()` already; the S3 upload hasn't.
        if (typeof err?.code !== 'number') toast.error('Failed to upload the file.');

        return false;
    }
};

const openFile = async (file: any) => {
    if (!props.entryId) return;

    try {
        const { data } = await companyDiaryApi.viewFile(props.entryId, file.id);
        window.open(data.download_url, '_blank', 'noopener');
    } catch {
        toast.error('Failed to open the file.');
    }
};

/** The files viewer's link: signed and inline, since entry files are private objects. */
const resolvePreviewUrl = async (file: any): Promise<string> => {
    const { data } = await companyDiaryApi.viewFile(props.entryId as number, file.id, { preview: true });

    return data.download_url;
};

/**
 * Streamed through the API rather than opened from the signed URL, so the saved
 * file carries the label the operator gave it.
 */
const downloadFile = async (file: any) => {
    if (!props.entryId) return;

    try {
        const response = await companyDiaryApi.downloadFile(props.entryId, file.id);
        const mimeType = response.headers['content-type'] || file.mime_type || 'application/octet-stream';
        const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));

        const link = document.createElement('a');
        link.href = url;
        link.download = `${getFileDisplayName(file)}.${getFileExtension(file) || 'bin'}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);
    } catch {
        toast.error('Failed to download the file.');
    }
};

const openEditModal = (file: any) => {
    fileToEdit.value = file;
    isEditModalOpen.value = true;
};

/**
 * No `shared_with_field_staff`: a diary entry has no order to share off, and its
 * chain carries a null `order_id` precisely so it cannot widen file access.
 */
const saveFile = (values: FileEditValues) =>
    companyDiaryApi.updateFile(props.entryId as number, fileToEdit.value.id, {
        display_label: values.display_label,
        tags: values.tags,
    });

const handleEditSaved = () => {
    fileToEdit.value = null;
    fetchFiles();
};

const confirmDelete = (file: any) => {
    fileToDelete.value = file;
    isDeleteModalOpen.value = true;
};

const removeFile = async () => {
    if (!props.entryId || !fileToDelete.value) return;

    isDeleting.value = true;
    try {
        await companyDiaryApi.destroyFile(props.entryId, fileToDelete.value.id);
        toast.success('File removed.');
        isDeleteModalOpen.value = false;
        fileToDelete.value = null;
        await fetchFiles();
    } catch {
        toast.error('Failed to remove the file.');
    } finally {
        isDeleting.value = false;
    }
};
</script>

<style scoped>
.list-move,
.list-enter-active,
.list-leave-active {
    transition:
        transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
        opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.list-enter-from {
    opacity: 0;
    transform: translateY(-8px);
}

.list-leave-to {
    opacity: 0;
    transform: translateX(20px) scale(0.98);
}

.list-leave-active {
    position: absolute;
    width: 100%;
}
</style>
